import { useEffect, useState } from 'react'
import { ArticleService } from 'admin/articleService'
import { TicketValidity, ticketValidity } from '../model/ticketValidity'

export type TicketValidityState = { loading: true } | { loading: false; validity: TicketValidity }

/**
 * The validity of each listed ticket, one request per ticket. The feed list
 * from backoffice carries no validity, and a ticket leaves the feed on its last
 * valid day, so the table needs it to warn ahead. Each row loads on its own.
 */
export function useTicketValidity(
    articleService: ArticleService,
    accountSlug: string,
    articleUuids: string[],
): Record<string, TicketValidityState> {
    const [states, setStates] = useState<Record<string, TicketValidityState>>({})
    const key = articleUuids.join(',')

    useEffect(() => {
        let cancelled = false
        const missing = articleUuids.filter((uuid) => !states[uuid])
        if (missing.length === 0) return
        setStates((current) => ({
            ...current,
            ...Object.fromEntries(missing.map((uuid) => [uuid, { loading: true } as TicketValidityState])),
        }))
        missing.forEach(async (uuid) => {
            try {
                const article = await articleService.fetchArticle(accountSlug, uuid)
                const validTo = article.validTo ? new Date(article.validTo) : null
                if (!cancelled) {
                    setStates((current) => ({
                        ...current,
                        [uuid]: { loading: false, validity: ticketValidity(validTo) },
                    }))
                }
            } catch {
                if (!cancelled) {
                    setStates((current) => ({ ...current, [uuid]: { loading: false, validity: { state: 'open' } } }))
                }
            }
        })
        return () => {
            cancelled = true
        }
    }, [key, accountSlug])

    return states
}
